
import { Injectable } from '@angular/core';
import { isKeyValEntryScoreData } from '@shared/KeyValStore';
import { scoreAchieveSchema, scoreDbSchema, scoreValueSchema } from '@shared/ragai.schemas';
import { z } from 'zod';
import { KeyValService } from '../key-val.service';
import { Score } from './Score';
export type AchieveMent = {
  value: number;
  skillId: string;
  message: string;
};
/**
 * Verwaltet Punktestände für benannte Scores (`RAGAI.SCORE`, `RAGAI.score`).
 * @remarks
 * Jeder Score-Name wird lazily zu einer eigenen {@link Score}-Instanz (siehe `./Score.ts`), die ihren Zustand
 * selbständig per `RAGAI.KEYVAL` (Typ passend zum Score-Namen) persistiert - `RAGAI.SCORE` selbst hält nur die
 * Map der bereits geladenen Scores und delegiert an `db()` bzw. den namenlosen Default-Score.
 * {@link achieve} deaktiviert als Nebeneffekt automatisch den übergebenen `skillId` per `RAGAI.SKILL.disableSkills()`
 * (verhindert, dass dieselbe Errungenschaft durch denselben Skill erneut ausgelöst wird) und zeigt bei
 * `autoMessage: true` automatisch einen Erfolgs-/Fehler-Toast, je nachdem ob `value` positiv oder negativ ist.
 * Werte werden erst nach {@link WaitForManagerService.varsInit} zuverlässig verfügbar sein, da sie asynchron
 * aus dem KeyVal-Speicher nachgeladen werden.
 */
@Injectable({ providedIn: 'root' })
export class ScoreService {
  public scores: Map<string, Score> = new Map<string, Score>();
  public initialsLoaded: boolean = false;
  constructor(private keyValService: KeyValService) {
    void this.loadInitialValues();
  }

  /** Wartet, bis die initialen Punktestände vom Server geladen wurden. */
  public async waitForInitials() {
    if (!this.initialsLoaded) {
      await this.loadInitialValues();
    }
  }

  /** @internal Lädt die initialen Punktestände aus dem KeyVal-Speicher. */
  public async loadInitialValues() {
    const initials = await this.keyValService.getInitialValues();
    initials.forEach((entry) => {
      if (isKeyValEntryScoreData(entry)) {
        this.db(entry.key).updateFromServer(entry);
      }
    });
    this.initialsLoaded = true;
  }


  /**
   * Gibt den benannten Score-Store zurück (erstellt ihn bei Bedarf).
   * @param scoreName Name des Scores, oder ein Objekt gemäß `scoreDbSchema`. Standard: der unbenannte Default-Score.
   * @returns Der Score-Store.
   */
  public db(scoreName: string | z.input<typeof scoreDbSchema> = ""): Score {
    const parsed = scoreDbSchema.safeParse(scoreName);
    if (parsed.success && typeof scoreName === 'object') {
      scoreName = parsed.data.name;
    }
    let store = this.scores.get(scoreName as string);
    if (store == null) {
      store = new Score(this.keyValService, scoreName as string);
      this.scores.set(scoreName as string, store);
    }
    return store;
  }

  /**
   * Erhöht den Default-Score und protokolliert eine Errungenschaft (Achievement).
   * @param value Anzahl der zu vergebenden Punkte, oder ein Objekt gemäß `scoreAchieveSchema`.
   * @param skillId ID des Skills, der die Errungenschaft ausgelöst hat.
   * @param message Anzeigetext für die Errungenschaft.
   * @param autoMessage `true`, um automatisch eine Standardnachricht zu generieren.
   */
  public achieve(value: number | z.input<typeof scoreAchieveSchema>, skillId?: string, message?: string, autoMessage: boolean = false): void {
    const parsed = scoreAchieveSchema.safeParse(value);
    if (parsed.success && typeof value === 'object') {
      skillId = parsed.data.skillId;
      message = parsed.data.message;
      autoMessage = parsed.data.autoMessage ?? autoMessage;
      value = parsed.data.value;
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.db().achieve(value as number, skillId!, message!, autoMessage);
  }

  /**
   * Gibt alle protokollierten Errungenschaften des Default-Scores zurück.
   * @param asJson `true`, um das Ergebnis als JSON-String statt als Array zu erhalten.
   * @returns Die Errungenschaften.
   */
  public getAchieveMents(asJson: boolean = false) {
    return this.db().getAchieveMents(asJson);
  }
  /**
   * Erhöht den Default-Score um den angegebenen Wert.
   * @param value Anzahl der hinzuzufügenden Punkte, oder ein Objekt gemäß `scoreValueSchema`.
   */
  public add(value: number | z.input<typeof scoreValueSchema>): void {
    const parsed = scoreValueSchema.safeParse(value);
    if (parsed.success && typeof value === 'object') {
      value = parsed.data.value;
    }
    this.db().add(value as number);
  }
  /**
   * Gibt den aktuellen Punktestand des Default-Scores zurück.
   * @returns Der aktuelle Punktestand.
   */
  public get() {
    return this.db().get();
  }
  /**
   * Setzt den Default-Score auf 0 zurück.
   * @returns Der zurückgesetzte Punktestand.
   */
  public reset() {
    return this.db().reset();
  }
  /**
   * Setzt den Default-Score auf einen festen Wert.
   * @param value Der neue Punktestand, oder ein Objekt gemäß `scoreValueSchema`.
   */
  public set(value: number | z.input<typeof scoreValueSchema>): void {
    const parsed = scoreValueSchema.safeParse(value);
    if (parsed.success && typeof value === 'object') {
      value = parsed.data.value;
    }
    this.db().set(value as number);
  }
  /**
   * Verringert den Default-Score um den angegebenen Wert.
   * @param value Anzahl der abzuziehenden Punkte, oder ein Objekt gemäß `scoreValueSchema`.
   */
  public sub(value: number | z.input<typeof scoreValueSchema>): void {
    const parsed = scoreValueSchema.safeParse(value);
    if (parsed.success && typeof value === 'object') {
      value = parsed.data.value;
    }
    this.db().sub(value as number);
  }
}
